import React, { useState, useEffect } from 'react';
import { clientLinkConfigAPI } from '../../services/api';
import { 
  Link, Save, RefreshCw, ToggleLeft, ToggleRight, Check
} from 'lucide-react';

const WidgetClientLinkConfig = () => {
  const [config, setConfig] = useState({
    enabled: true,
    button_label: 'Lien client',
    modal_title: 'Lien de complétion client',
    modal_message: 'Envoyez ce lien au client pour qu\'il complète ses informations.',
    success_message: 'Lien copié avec succès !'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await clientLinkConfigAPI.getConfig();
      setConfig(res.data);
    } catch (error) {
      console.error('Erreur chargement config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleToggle = async () => {
    try {
      const res = await clientLinkConfigAPI.toggle();
      setConfig(prev => ({ ...prev, enabled: res.data.enabled }));
    } catch (error) {
      console.error('Erreur toggle:', error);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await clientLinkConfigAPI.updateConfig(config);
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetConfig = async () => {
    if (!window.confirm('Réinitialiser la configuration par défaut ?')) return;
    try {
      const res = await clientLinkConfigAPI.reset();
      setConfig(res.data);
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur reset:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Link className="w-6 h-6 text-blue-600" />
            Configuration du lien client
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configurez le bouton et le modal de génération de lien pour les clients
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetConfig}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 px-3 py-2"
          >
            <RefreshCw className="w-4 h-4" />
            Réinitialiser
          </button>
          {hasChanges && (
            <button
              onClick={saveConfig}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="save-client-link-config"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          )}
        </div>
      </div>

      {/* Toggle activation */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Activer le lien client</h3>
            <p className="text-sm text-gray-500 mt-1">
              Affiche le bouton "Lien client" sur les fiches dossier
            </p>
          </div>
          <button
            onClick={handleToggle}
            className={`p-2 rounded-lg transition ${
              config.enabled 
                ? 'bg-green-100 text-green-600' 
                : 'bg-gray-100 text-gray-400'
            }`}
            data-testid="toggle-client-link"
          >
            {config.enabled ? (
              <ToggleRight className="w-8 h-8" />
            ) : (
              <ToggleLeft className="w-8 h-8" />
            )}
          </button>
        </div>
        <div className={`mt-4 px-4 py-3 rounded-lg ${config.enabled ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-gray-600'}`}>
          {config.enabled ? (
            <p className="flex items-center gap-2"><Check className="w-4 h-4" /> Le bouton "Lien client" est visible sur les fiches dossier</p>
          ) : (
            <p>Le bouton "Lien client" est masqué</p>
          )}
        </div>
      </div>

      {/* Configuration des textes */}
      {config.enabled && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <h3 className="font-semibold text-gray-900 mb-4">Personnalisation des textes</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Libellé du bouton</label>
            <input
              type="text"
              value={config.button_label}
              onChange={(e) => handleChange('button_label', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Lien client"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre du modal</label>
            <input
              type="text"
              value={config.modal_title}
              onChange={(e) => handleChange('modal_title', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Lien de complétion client"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message du modal</label>
            <textarea
              value={config.modal_message}
              onChange={(e) => handleChange('modal_message', e.target.value)}
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Envoyez ce lien au client..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message de succès</label>
            <input
              type="text"
              value={config.success_message}
              onChange={(e) => handleChange('success_message', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Lien copié avec succès !"
            />
          </div>
        </div>
      )}

      {/* Prévisualisation */}
      {config.enabled && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Prévisualisation du modal</h3>
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900">{config.modal_title}</h4>
            </div>
            <p className="text-gray-600 mb-4">{config.modal_message}</p>
            <div className="bg-gray-100 rounded-lg p-3 font-mono text-sm text-gray-700 break-all">
              https://socialac.fr/client/abc123xyz...
            </div>
            <div className="flex gap-2 mt-4">
              <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium">
                📋 Copier le lien
              </button>
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p>
          <strong>Comment ça fonctionne :</strong> Le gestionnaire clique sur "{config.button_label}" sur une fiche dossier. 
          Un lien unique est généré et peut être envoyé au client. Le client peut alors compléter ses informations 
          via ce lien sans avoir besoin de se connecter.
        </p>
      </div>
    </div>
  );
};

export default WidgetClientLinkConfig;
